(() => {
  if (customElements.get('pop-main-shape-example')) return;
  const images = new Map();
  const loadImage = (url, template) => {
    const key = JSON.stringify([url, template.crossOrigin, template.referrerPolicy]);
    if (!images.has(key)) {
      const image = new Image();
      image.decoding = 'async';
      if (template.crossOrigin !== null) image.crossOrigin = template.crossOrigin;
      if (template.referrerPolicy) image.referrerPolicy = template.referrerPolicy;
      const ready = new Promise((resolve, reject) => {
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Example image unavailable'));
        image.src = url;
      }).then(async (loaded) => {
        if (typeof loaded.decode === 'function') await loaded.decode();
        return loaded;
      }).catch((error) => { images.delete(key); throw error; });
      images.set(key, ready);
    }
    return images.get(key);
  };
  const prepareImage = async (template, url) => {
    const image = (await loadImage(url, template)).cloneNode(false);
    for (const { name, value } of template.attributes) {
      if (['id', 'class', 'style', 'width', 'height', 'alt', 'title', 'role', 'data-pop-shape-example-before', 'data-pop-shape-example-after'].includes(name) || name.startsWith('aria-')) {
        image.setAttribute(name, value);
      }
    }
    image.loading = 'eager';
    // Decode the exact nodes that will be inserted, so neither visible image reloads during the swap.
    if (typeof image.decode === 'function') await image.decode();
    else if (!image.complete) await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error('Example image unavailable'));
    });
    if (!image.complete || !image.naturalWidth || !image.naturalHeight) throw new Error('Example image unavailable');
    return image;
  };
  const presentationKey = (before, after, canvas) => JSON.stringify([
    before.style.aspectRatio,
    canvas.style.getPropertyValue('--pop-canvas-ratio'),
    canvas.style.getPropertyValue('--pop-canvas-image'),
    before.width, before.height, before.alt,
    after.width, after.height, after.alt,
  ]);
  const changedStyle = (record, properties) => {
    const previous = document.createElement('span').style;
    previous.cssText = record.oldValue || '';
    return properties.some((property) => previous.getPropertyValue(property) !== record.target.style.getPropertyValue(property));
  };
  customElements.define('pop-main-shape-example', class extends HTMLElement {
    #controller;
    #observer;
    #revision = 0;
    #queued = false;
    #applied;

    connectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#controller = new AbortController();
      const block = this.closest('[data-personalisation-block]');
      block?.addEventListener('pop:portrait-shape-change', () => this.#queueSync(), { signal: this.#controller.signal });
      this.#observer = new MutationObserver((records) => {
        if (records.some((record) => {
          if (record.type === 'attributes') {
            if (record.target === this) return record.attributeName === 'data-example-shape';
            if (record.target.matches('[data-pop-shape-example-canvas]')) {
              return record.attributeName === 'style' && changedStyle(record, ['--pop-canvas-ratio', '--pop-canvas-image']);
            }
            if (!record.target.matches('[data-pop-shape-example-before], [data-pop-shape-example-after]')) return false;
            return record.attributeName === 'style'
              ? record.target.hasAttribute('data-pop-shape-example-before') && changedStyle(record, ['aspect-ratio'])
              : ['src', 'width', 'height', 'alt'].includes(record.attributeName);
          }
          return record.target.matches?.('[data-pop-shape-example-config]') ||
            [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 &&
              (node.matches('figure, img, [data-pop-shape-example-config], [data-pop-shape-example-canvas]') ||
                node.querySelector('[data-pop-shape-example-before], [data-pop-shape-example-after], [data-pop-shape-example-config]')));
        })) this.#queueSync();
      });
      this.#observer.observe(this, {
        childList: true, subtree: true, attributes: true, attributeOldValue: true,
        attributeFilter: ['src', 'style', 'width', 'height', 'alt', 'data-example-shape'],
      });
      this.#queueSync();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#revision += 1;
    }

    #queueSync() {
      if (this.#queued) return;
      this.#queued = true;
      queueMicrotask(() => { this.#queued = false; void this.#sync(); });
    }

    async #sync() {
      if (!this.isConnected) return;
      const block = this.closest('[data-personalisation-block]');
      const shape = window.PopPrintsPortraitShapes?.getShape(block) || 'portrait';
      const before = this.querySelector('[data-pop-shape-example-before]');
      const after = this.querySelector('[data-pop-shape-example-after]');
      const canvas = this.querySelector('[data-pop-shape-example-canvas]');
      if (!block || !before || !after || !canvas) return;
      let entry;
      try { entry = JSON.parse(this.querySelector('[data-pop-shape-example-config]')?.textContent || '{}')[shape]; } catch { return; }
      if (!entry?.before || !entry.after || !Array.isArray(entry.ratio) || !entry.ratio.every((value) => Number(value) > 0)) return;
      const revision = ++this.#revision;
      const entryKey = JSON.stringify([shape, entry.before, entry.after, entry.ratio, entry.beforeAlt, entry.afterAlt]);
      if (this.dataset.exampleShape === shape && before.getAttribute('src') === entry.before && after.getAttribute('src') === entry.after) {
        const currentPresentation = presentationKey(before, after, canvas);
        // Leave the initial Portrait markup untouched; later refreshes must also preserve its presentation.
        if (!this.#applied && shape === 'portrait') this.#applied = { entryKey, presentationKey: currentPresentation };
        if (this.#applied?.entryKey === entryKey && this.#applied.presentationKey === currentPresentation) {
          this.removeAttribute('aria-busy');
          return;
        }
      }
      this.setAttribute('aria-busy', 'true');
      this.removeAttribute('data-example-load-error');
      try {
        const loaded = await Promise.all([prepareImage(before, entry.before), prepareImage(after, entry.after)]);
        if (!this.isConnected || revision !== this.#revision ||
          this.querySelector('[data-pop-shape-example-before]') !== before ||
          this.querySelector('[data-pop-shape-example-after]') !== after ||
          this.querySelector('[data-pop-shape-example-canvas]') !== canvas) return;
        if (window.PopPrintsPortraitShapes?.getShape(block) !== shape) return;
        const ratio = entry.ratio.join(' / ');
        const canvasImage = `url(${JSON.stringify(entry.after)})`;
        loaded[0].style.aspectRatio = ratio;
        loaded.forEach((image, index) => {
          const width = shape === 'portrait' ? 240 : loaded[index].naturalWidth;
          const height = shape === 'portrait' ? 360 : loaded[index].naturalHeight;
          if (image.width !== width) image.width = width;
          if (image.height !== height) image.height = height;
          const alt = index ? entry.afterAlt : entry.beforeAlt;
          if (alt && image.alt !== alt) image.alt = alt;
        });
        // Both replacements and their layout commit within one task, before the browser can paint.
        before.replaceWith(loaded[0]);
        after.replaceWith(loaded[1]);
        if (canvas.style.getPropertyValue('--pop-canvas-ratio') !== ratio) canvas.style.setProperty('--pop-canvas-ratio', ratio);
        if (canvas.style.getPropertyValue('--pop-canvas-image') !== canvasImage) canvas.style.setProperty('--pop-canvas-image', canvasImage);
        if (this.dataset.exampleShape !== shape) this.dataset.exampleShape = shape;
        this.#applied = { entryKey, presentationKey: presentationKey(loaded[0], loaded[1], canvas) };
        this.removeAttribute('aria-busy');
      } catch {
        if (revision !== this.#revision) return;
        this.dataset.exampleLoadError = shape;
        this.removeAttribute('aria-busy');
      }
    }
  });
})();
